import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, MessageCircle } from "lucide-react";
import Logo from "./Logo";
import { scrollToSection } from "./LenisProvider";
import { WHATSAPP_URL } from "./constants";

const LINKS = [
  { id: "inicio", label: "Início" },
  { id: "servicos", label: "Serviços" },
  { id: "empresa", label: "A Empresa" },
  { id: "contato", label: "Contato" },
];

export default function Navbar({ start = true }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = (id) => {
    setOpen(false);
    setTimeout(() => scrollToSection(id), open ? 250 : 0);
  };

  return (
    <>
      <motion.header
        initial={{ y: -70, opacity: 0 }}
        animate={start ? { y: 0, opacity: 1 } : { y: -70, opacity: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
          scrolled
            ? "glass shadow-[0_8px_32px_rgba(0,10,30,0.6)]"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="mx-auto max-w-7xl px-5 lg:px-8 h-[72px] flex items-center justify-between">
          <button
            data-testid="nav-logo-button"
            onClick={() => goTo("inicio")}
            className="flex items-center hover:opacity-80 transition-opacity"
            aria-label="Ir para o início"
          >
            <Logo className="h-9" />
          </button>

          <nav className="hidden lg:flex items-center gap-9">
            {LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-link-${l.id}`}
                onClick={() => goTo(l.id)}
                className="text-sm font-medium text-slate-300 hover:text-white transition-colors relative group"
              >
                {l.label}
                <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-dm-glow transition-all duration-300 group-hover:w-full" />
              </button>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <a
              data-testid="nav-whatsapp-button"
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="WhatsApp"
              className="h-10 w-10 rounded-full border border-white/10 flex items-center justify-center text-slate-300 hover:text-white hover:border-dm-glow/60 hover:bg-white/5 transition-all"
            >
              <MessageCircle size={17} />
            </a>
            <button
              data-testid="nav-cta-orcamento"
              onClick={() => goTo("contato")}
              className="rounded-full bg-dm-blue hover:bg-[#0052d9] text-white text-sm font-semibold px-6 py-2.5 shadow-[0_0_24px_rgba(0,98,255,0.45)] transition-all hover:shadow-[0_0_36px_rgba(0,98,255,0.7)] hover:-translate-y-0.5"
            >
              Orçamento
            </button>
          </div>

          <button
            data-testid="mobile-menu-button"
            className="lg:hidden h-10 w-10 rounded-lg border border-white/10 flex items-center justify-center text-slate-200"
            onClick={() => setOpen((v) => !v)}
            aria-label="Abrir menu"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-40 bg-ink/95 backdrop-blur-xl lg:hidden flex flex-col items-center justify-center gap-8"
          >
            {LINKS.map((l, i) => (
              <motion.button
                key={l.id}
                initial={{ y: 24, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.08 * i, duration: 0.4 }}
                onClick={() => goTo(l.id)}
                className="font-display text-3xl font-bold text-slate-100 hover:text-dm-glow transition-colors"
              >
                {l.label}
              </motion.button>
            ))}
            <motion.button
              initial={{ y: 24, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.35, duration: 0.4 }}
              data-testid="mobile-cta-orcamento"
              onClick={() => goTo("contato")}
              className="mt-4 rounded-full bg-dm-blue text-white font-semibold px-8 py-3.5"
            >
              Solicitar Orçamento
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
